// UserRateHistory.jsx
// Component to display and manage user payment rate history
// Similar to ServiceRateHistory.jsx but for user hourly/mileage rates

import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/dateFormatter";

const UserRateHistory = ({ userId, userName, rateType = "hourly" }) => {
  const { user: currentUser } = useContext(AuthContext);
  const isAdmin = currentUser?.role === "admin";

  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [earliestUnpaidInfo, setEarliestUnpaidInfo] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    rate: "",
    effective_date: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const rateLabel = rateType === "hourly" ? "Hourly Rate" : "Mileage Rate";
  const rateUnit = rateType === "hourly" ? "/hour" : "/mile";

  useEffect(() => {
    if (userId) {
      fetchRates();
      fetchEarliestUnpaidDate();
    }
  }, [userId, rateType]);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const res = await secureAxios.get(`/api/users/${userId}/rates`, {
        params: { rate_type: rateType },
      });
      setRates(res.data.rates || []);
    } catch (err) {
      console.error("Error fetching rates:", err);
      toast.error("Failed to load rate history");
    } finally {
      setLoading(false);
    }
  };

  const fetchEarliestUnpaidDate = async () => {
    try {
      const res = await secureAxios.get(
        `/api/users/${userId}/rates/earliest-unpaid`,
        {
          params: { rate_type: rateType },
        }
      );
      setEarliestUnpaidInfo(res.data);
    } catch (err) {
      console.error("Error fetching earliest unpaid date:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      rate: "",
      effective_date: new Date().toISOString().split("T")[0],
      notes: "",
    });
    setShowAddForm(false);
    setEditingRate(null);
  };

  const handleSubmit = async () => {
    if (!formData.rate || parseFloat(formData.rate) < 0) {
      toast.error("Please enter a valid rate (must be >= 0)");
      return;
    }

    if (!formData.effective_date) {
      toast.error("Please select an effective date");
      return;
    }

    try {
      if (editingRate) {
        // Update existing rate
        await secureAxios.put(`/api/user-rates/${editingRate.rate_id}`, {
          rate: parseFloat(formData.rate),
          effective_date: formData.effective_date,
          notes: formData.notes,
        });
        toast.success("Rate updated successfully");
      } else {
        // Create new rate
        await secureAxios.post(`/api/users/${userId}/rates`, {
          rate: parseFloat(formData.rate),
          rate_type: rateType,
          effective_date: formData.effective_date,
          notes: formData.notes,
        });
        toast.success("New rate added successfully");
      }

      resetForm();
      fetchRates();
      fetchEarliestUnpaidDate();
    } catch (err) {
      console.error("Error saving rate:", err);
      const errorMessage = err.response?.data?.error || "Failed to save rate";
      toast.error(errorMessage);
    }
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      rate: rate.rate.toString(),
      effective_date:
        typeof rate.effective_date === "string"
          ? rate.effective_date.split("T")[0]
          : new Date(rate.effective_date).toISOString().split("T")[0],
      notes: rate.notes || "",
    });
    setShowAddForm(true);
  };

  const handleDelete = async (rate) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this ${rateType} rate ($${
          rate.rate
        }) effective ${formatDate(rate.effective_date)}?`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(`/api/user-rates/${rate.rate_id}`);
      toast.success("Rate deleted successfully");
      fetchRates();
      fetchEarliestUnpaidDate();
    } catch (err) {
      console.error("Error deleting rate:", err);
      const errorMessage = err.response?.data?.error || "Failed to delete rate";
      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (rate) => {
    if (rate.is_current) {
      return <span className="badge bg-success">Current</span>;
    } else if (rate.is_future) {
      return <span className="badge bg-info">Future</span>;
    } else if (rate.is_past) {
      return <span className="badge bg-secondary">Past</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <div
          className="spinner-border spinner-border-sm text-primary"
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="ms-2">Loading rate history...</span>
      </div>
    );
  }

  return (
    <div className="user-rate-history">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">
          <i
            className={`bi bi-${
              rateType === "hourly" ? "clock" : "car-front"
            } me-2`}
          ></i>
          {rateLabel} History
        </h6>
        {isAdmin && !showAddForm && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add New Rate
          </button>
        )}
      </div>

      {/* Backdating Info */}
      {earliestUnpaidInfo && (
        <div className="alert alert-info py-2 small mb-3">
          <i className="bi bi-info-circle me-1"></i>
          {earliestUnpaidInfo.message}
        </div>
      )}

      {/* Add/Edit Form - Note: Uses div instead of form to avoid nested form issues */}
      {showAddForm && (
        <div className="card mb-3">
          <div className="card-header py-2">
            <strong>{editingRate ? "Edit Rate" : "Add New Rate"}</strong>
          </div>
          <div className="card-body">
            <div>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">
                    Rate ($){rateUnit} <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      name="rate"
                      value={formData.rate}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                    <span className="input-group-text">{rateUnit}</span>
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Effective Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="effective_date"
                    value={formData.effective_date}
                    onChange={handleInputChange}
                    disabled={editingRate && !editingRate.is_future}
                  />
                  {editingRate && !editingRate.is_future && (
                    <small className="text-muted">
                      Cannot change date for current/past rates
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>

              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-success me-2"
                  onClick={handleSubmit}
                >
                  <i className="bi bi-check-circle me-1"></i>
                  {editingRate ? "Update Rate" : "Save Rate"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate History Table */}
      {rates.length === 0 ? (
        <div className="alert alert-secondary">
          <i className="bi bi-info-circle me-2"></i>
          No rate history found. The user's current rate from their profile will
          be used.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead className="table-light">
              <tr>
                <th>Status</th>
                <th>Rate</th>
                <th>Effective Date</th>
                <th>End Date</th>
                <th>Notes</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rates.map((rate) => (
                <tr
                  key={rate.rate_id}
                  className={rate.is_current ? "table-success" : ""}
                >
                  <td>{getStatusBadge(rate)}</td>
                  <td>
                    <strong>${parseFloat(rate.rate).toFixed(2)}</strong>
                    <small className="text-muted">{rateUnit}</small>
                  </td>
                  <td>{formatDate(rate.effective_date)}</td>
                  <td>
                    {rate.end_date ? (
                      formatDate(rate.end_date)
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <small>{rate.notes || "-"}</small>
                  </td>
                  {isAdmin && (
                    <td>
                      <div className="btn-group btn-group-sm">
                        {/* Can only edit current or future rates */}
                        {(rate.is_current || rate.is_future) && (
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(rate)}
                            title="Edit Rate"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                        {/* Can delete if: future rate, OR no paid logs exist (and not the only rate) */}
                        {(rate.is_future || (earliestUnpaidInfo && !earliestUnpaidInfo.latestPaidDate)) && rates.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(rate)}
                            title="Delete Rate"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Help Text */}
      <div className="small text-muted mt-2">
        <i className="bi bi-question-circle me-1"></i>
        <strong>How it works:</strong> When processing payments, the system uses
        the rate that was effective on the date of service/trip. This allows you
        to schedule future rate changes or correct historical rates (if no
        payments have been made).
      </div>
    </div>
  );
};

export default UserRateHistory;